/**
 * Utility functions for handling standardized API responses
 */

/**
 * Processes an API response to handle the standardized format
 * 
 * @param {Object} response - The axios response object
 * @returns {Object} Processed response with success, data, count, and message fields
 */
export const processApiResponse = (response) => {
  // Handle standardized response format
  if (response && response.data) {
    if (response.data.success === true) {
      return {
        success: true,
        data: response.data.data,
        count: response.data.count,
        message: response.data.message,
        pagination: response.data.pagination
      };
    } else if (response.data.success === false) {
      return {
        success: false,
        error: response.data.error,
        message: response.data.message
      };
    }
  }
  
  // Handle legacy response format (for backward compatibility)
  return {
    success: true,
    data: response.data
  };
};

/**
 * Handles API errors in a standardized way
 * 
 * @param {Error} error - The error object from axios
 * @returns {Object} Standardized error object
 */
export const handleApiError = (error) => {
  if (error.response && error.response.data) {
    if (error.response.data.success === false) {
      return {
        success: false,
        error: error.response.data.error,
        message: error.response.data.message || 'An error occurred'
      };
    }
    return {
      success: false,
      error: 'API Error',
      message: error.response.data.message || error.response.data.error || error.message
    };
  }
  
  if (error.request) {
    return {
      success: false,
      error: 'Network Error',
      message: 'No response received from server. Check your network connection.'
    };
  }
  
  return {
    success: false,
    error: 'Request Error',
    message: error.message || 'Unknown error occurred'
  };
};

/**
 * Extracts error message from an API error response
 * 
 * @param {Error} error - The error object
 * @returns {string} Human-readable error message
 */
export const getErrorMessage = (error) => {
  const processedError = handleApiError(error);
  return processedError.message;
};

/**
 * Handles pagination for collection endpoints
 * 
 * @param {Object} response - The processed API response
 * @returns {Object} Pagination information
 */
export const extractPagination = (response) => {
  if (response && response.pagination) {
    return response.pagination;
  }
  
  return {
    nextToken: null,
    hasMore: false
  };
};
